import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, CalendarClock, ShieldCheck } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { COMPANY } from "@/data/content";
import { API_BASE } from "@/lib/apiBase";

const API = API_BASE;

const SELECTS = {
  years_in_business: ["Less than 1 year", "1–3 years", "3–5 years", "5–10 years", "10+ years"],
  business_type: ["Jet Ski rental", "Pontoon / boat rental", "Watersports operator", "Waterfront marina", "Resort / hotel", "Other marine business"],
  water_access_type: ["Ocean / Gulf", "Bay / Intracoastal", "Recreational lake", "River", "Other"],
  dock_launch_access: ["Private dock", "Marina slip", "Boat ramp / launch", "Beach launch", "To be arranged"],
  rental_units_count: ["1–5", "6–10", "11–20", "21–50", "50+"],
  months_of_operation: ["Year-round", "8–11 months", "5–7 months", "Seasonal (under 5 months)"],
  weekly_customer_volume: ["Under 50", "50–150", "150–400", "400+"],
  staff_count: ["1–3", "4–8", "9–15", "16+"],
  commercial_insurance: ["Yes — current commercial marine/liability policy", "Yes — general commercial policy", "In progress", "Not yet"],
  available_storage: ["Yes — secure on-site storage", "Yes — off-site storage", "Limited", "To be arranged"],
  staff_availability: ["Dedicated staff available now", "Staff available with notice", "Hiring required", "To be determined"],
  deployment_timeline: ["As soon as possible", "Within 3 months", "3–6 months", "6+ months"],
  marketing_addons_interest: ["Very interested", "Somewhat interested", "Need more information", "Not interested"],
};

const STEPS = [
  {
    id: "contact",
    title: "Contact Information",
    fields: [
      { name: "first_name", label: "First name", half: true },
      { name: "last_name", label: "Last name", half: true },
      { name: "job_title", label: "Job title" },
      { name: "email", label: "Email", type: "email", half: true },
      { name: "phone", label: "Direct phone number", type: "tel", half: true },
    ],
  },
  {
    id: "business",
    title: "Business Information",
    fields: [
      { name: "legal_business_name", label: "Legal business name" },
      { name: "dba", label: "DBA (if applicable)", optional: true, half: true },
      { name: "website", label: "Website", optional: true, type: "url", half: true, placeholder: "https://" },
      { name: "business_address", label: "Business address" },
      { name: "city", label: "City", half: true },
      { name: "state", label: "State", half: true },
      { name: "zip_code", label: "ZIP code", half: true },
      { name: "years_in_business", label: "Years in business", half: true, select: true },
      { name: "business_type", label: "Business type", select: true },
    ],
  },
  {
    id: "waterfront",
    title: "Waterfront Operation",
    fields: [
      { name: "waterfront_address", label: "Waterfront location address" },
      { name: "water_access_type", label: "Type of water access", select: true, half: true },
      { name: "dock_launch_access", label: "Dock or launch access", select: true, half: true },
      { name: "current_rental_products", label: "Current rental products", placeholder: "e.g. Jet Skis, pontoons, kayaks" },
      { name: "rental_units_count", label: "Number of rental units", select: true, half: true },
      { name: "months_of_operation", label: "Months of operation", select: true, half: true },
      { name: "weekly_customer_volume", label: "Typical weekly customer volume", select: true, half: true },
      { name: "staff_count", label: "Operating staff count", select: true, half: true },
    ],
  },
  {
    id: "readiness",
    title: "Readiness",
    fields: [
      { name: "booking_platform", label: "Current booking platform", placeholder: "e.g. FareHarbor, Peek Pro, custom" },
      { name: "commercial_insurance", label: "Commercial insurance", select: true, half: true },
      { name: "available_storage", label: "Available storage", select: true, half: true },
      { name: "staff_availability", label: "Staff availability", select: true, half: true },
      { name: "deployment_timeline", label: "Preferred deployment timeline", select: true, half: true },
      { name: "marketing_addons_interest", label: "Interest in marketing add-ons", select: true },
      { name: "additional_info", label: "Additional information", optional: true, textarea: true },
    ],
  },
];

const CONSENTS = [
  { name: "consent_contact", label: "I give Xtreme Exotics Watersports LLC permission to contact me about this application." },
  { name: "consent_accurate", label: "I confirm the information submitted is accurate to the best of my knowledge." },
  { name: "consent_no_guarantee", label: "I acknowledge that applying does not guarantee approval or deployment." },
  { name: "consent_privacy", label: "I agree to the Privacy Policy.", link: { text: "Privacy Policy", href: "/privacy-policy" } },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyForm = () => {
  const base = { fax_number: "" };
  STEPS.forEach((s) => s.fields.forEach((f) => (base[f.name] = "")));
  CONSENTS.forEach((c) => (base[c.name] = false));
  return base;
};

export default function Application() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const current = STEPS[step];
  const progress = useMemo(() => Math.round(((step + 1) / STEPS.length) * 100), [step]);

  const set = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const validateStep = () => {
    const errs = {};
    current.fields.forEach((f) => {
      const v = (form[f.name] || "").trim();
      if (!f.optional && !v) errs[f.name] = "Required";
      if (f.name === "email" && v && !EMAIL_RE.test(v)) errs[f.name] = "Enter a valid email";
      if (f.name === "phone" && v && v.replace(/\D/g, "").length < 7) errs[f.name] = "Enter a valid phone number";
    });
    if (step === STEPS.length - 1) {
      CONSENTS.forEach((c) => {
        if (!form[c.name]) errs[c.name] = "Required";
      });
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = () => {
    if (!validateStep()) {
      toast.error("Please complete the highlighted fields.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.__lenis?.scrollTo(0, { duration: 0.6 });
  };

  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!validateStep()) {
      toast.error("Please complete the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/fleet-applications`, form);
      setResult(data.application_id);
      window.__lenis?.scrollTo(0, { immediate: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Submission failed. Please try again or contact us directly.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (f) => {
    const err = errors[f.name];
    const base = f.half ? "sm:col-span-1" : "sm:col-span-2";
    const span = f.textarea ? "sm:col-span-2" : f.select && !f.half ? "sm:col-span-2" : base;
    return (
      <div key={f.name} className={span}>
        <label htmlFor={`field-${f.name}`} className="field-label">
          {f.label} {f.optional && <span className="normal-case tracking-normal text-muted/50">(optional)</span>}
        </label>
        {f.select ? (
          <select
            id={`field-${f.name}`}
            className={`field appearance-none ${err ? "border-red-400/70" : ""}`}
            value={form[f.name]}
            onChange={(e) => set(f.name, e.target.value)}
            data-testid={`app-field-${f.name}`}
          >
            <option value="">Select…</option>
            {(SELECTS[f.name] || []).map((o) => (
              <option key={o} value={o} className="bg-charcoal">{o}</option>
            ))}
          </select>
        ) : f.textarea ? (
          <textarea
            id={`field-${f.name}`}
            rows={4}
            className={`field resize-none ${err ? "border-red-400/70" : ""}`}
            value={form[f.name]}
            onChange={(e) => set(f.name, e.target.value)}
            data-testid={`app-field-${f.name}`}
          />
        ) : (
          <input
            id={`field-${f.name}`}
            type={f.type || "text"}
            placeholder={f.placeholder || ""}
            className={`field ${err ? "border-red-400/70" : ""}`}
            value={form[f.name]}
            onChange={(e) => set(f.name, e.target.value)}
            data-testid={`app-field-${f.name}`}
          />
        )}
        {err && <p className="mt-1 text-xs text-red-400" role="alert">{err}</p>}
      </div>
    );
  };

  return (
    <div data-testid="application-page">
      <Navbar />
      <main className="relative bg-ink pb-28 pt-32 lg:pt-40">
        <div className="spotlight absolute inset-x-0 top-0 h-[480px]" />
        <div className="container-x relative max-w-3xl">
          <AnimatePresence mode="wait">
            {result ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="card-line rounded-2xl p-10 text-center sm:p-14"
                data-testid="app-success-state"
              >
                <span className="mx-auto grid h-16 w-16 place-items-center rounded-full border border-aqua/40 bg-aqua/10 text-aqua">
                  <CheckCircle2 size={30} />
                </span>
                <h1 className="mt-7 font-display text-3xl font-extrabold tracking-tight text-offwhite sm:text-4xl">
                  Thank You for Applying
                </h1>
                <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-muted">
                  Your Fleet Partner application has been received. {COMPANY.name} will review your information and
                  contact qualified applicants regarding the next step.
                </p>
                <p className="mt-5 font-mono text-xs tracking-[0.2em] text-muted/70">
                  Application reference: <span className="text-aqua" data-testid="app-success-id">{result}</span>
                </p>
                <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
                  <a
                    href={`mailto:${COMPANY.email}?subject=Discovery%20Call%20—%20Fleet%20Partner%20Application%20${result}`}
                    className="btn-primary"
                    data-testid="schedule-discovery-btn"
                  >
                    <CalendarClock size={16} /> Schedule a Discovery Call
                  </a>
                  <Link to="/#program" className="btn-ghost" data-testid="return-program-btn">
                    Return to Fleet Partner Program
                  </Link>
                </div>
              </motion.div>
            ) : (
              <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <p className="eyebrow mb-4">Fleet Partner Application</p>
                <h1 className="font-display text-3xl font-extrabold tracking-tight text-offwhite sm:text-4xl" data-testid="app-title">
                  Apply to Become a <span className="text-aqua">Fleet Partner</span>
                </h1>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-muted">
                  Four short steps. Our team reviews every application and contacts qualified operators about the next
                  step. Applying does not guarantee approval or deployment.
                </p>

                <Link
                  to="/pipeline/apply"
                  className="card-line mt-6 flex items-center justify-between gap-4 rounded-xl bg-white/60 p-4 transition-colors hover:border-aqua/50"
                  data-testid="plan-future-season-link"
                >
                  <div>
                    <p className="font-display text-sm font-bold text-offwhite">Not ready this season?</p>
                    <p className="mt-0.5 text-xs text-muted">
                      Join the Future Deployment / Seasonal Partner Pipeline and reserve your place in line for a future season.
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full border border-aqua/40 px-4 py-2 font-display text-[11px] font-bold uppercase tracking-wider text-aqua">
                    Plan for a Future Season
                  </span>
                </Link>

                <div className="mt-10" data-testid="app-progress">
                  <div className="mb-3 flex items-center justify-between">
                    {STEPS.map((s, i) => (
                      <div key={s.id} className="flex items-center gap-2">
                        <span
                          className={`grid h-7 w-7 place-items-center rounded-full border font-mono text-[11px] transition-colors duration-300 ${
                            i < step
                              ? "border-aqua bg-aqua text-[#052726]"
                              : i === step
                                ? "border-aqua text-aqua"
                                : "border-black/15 text-muted/60"
                          }`}
                        >
                          {i + 1}
                        </span>
                        <span className={`hidden text-xs font-semibold sm:block ${i === step ? "text-offwhite" : "text-muted/60"}`}>
                          {s.title}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="h-1 overflow-hidden rounded-full bg-black/[0.07]">
                    <div className="h-full bg-aqua transition-[width] duration-500" style={{ width: `${progress}%` }} />
                  </div>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={current.id}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    className="card-line mt-8 rounded-2xl p-7 sm:p-10"
                    data-testid={`app-step-${current.id}`}
                  >
                    <h2 className="font-display text-xl font-bold text-offwhite">
                      Step {step + 1}: {current.title}
                    </h2>
                    {current.id === "readiness" && (
                      <p className="mt-4 rounded-lg border border-aqua/30 bg-aqua/[0.07] p-3 text-xs leading-relaxed text-muted" data-testid="app-insurance-note">
                        <b className="text-offwhite">Insurance is covered by Xtreme Exotics.</b> Every Jet Car operates
                        under Xtreme Exotics' commercial watercraft/rental liability and physical-damage coverage
                        (XINSURANCE fleet-partnership agreements), and the partner is added as an additional insured.
                        The partner contributes 45% of any deductible, capped at $2,500 — Xtreme Exotics covers the balance.
                      </p>
                    )}
                    <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2">
                      {current.fields.map(renderField)}
                    </div>

                    {/* Honeypot — hidden from humans */}
                    <input
                      type="text"
                      name="fax_number"
                      value={form.fax_number}
                      onChange={(e) => set("fax_number", e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      className="pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0"
                    />

                    {step === STEPS.length - 1 && (
                      <div className="mt-8 space-y-3.5 border-t border-black/[0.07] pt-7">
                        <p className="mb-1 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-muted">
                          <ShieldCheck size={14} className="text-aqua" /> Required acknowledgments
                        </p>
                        {CONSENTS.map((c) => (
                          <label key={c.name} className="flex cursor-pointer items-start gap-3 text-sm leading-snug text-muted">
                            <input
                              type="checkbox"
                              checked={form[c.name]}
                              onChange={(e) => set(c.name, e.target.checked)}
                              className="mt-0.5 h-4.5 w-4.5 shrink-0 accent-[#00FCF5]"
                              data-testid={`app-consent-${c.name}`}
                            />
                            <span className={errors[c.name] ? "text-red-400" : ""}>
                              {c.link ? (
                                <>
                                  I agree to the{" "}
                                  <a href={c.link.href} target="_blank" rel="noopener noreferrer" className="text-aqua underline underline-offset-2">
                                    {c.link.text}
                                  </a>
                                  .
                                </>
                              ) : (
                                c.label
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}

                    <div className="mt-9 flex items-center justify-between gap-4">
                      {step > 0 ? (
                        <button onClick={back} className="btn-ghost !px-5" data-testid="app-back-btn">
                          <ArrowLeft size={15} /> Back
                        </button>
                      ) : (
                        <span />
                      )}
                      {step < STEPS.length - 1 ? (
                        <button onClick={next} className="btn-primary" data-testid="app-next-btn">
                          Continue <ArrowRight size={15} />
                        </button>
                      ) : (
                        <button onClick={submit} disabled={submitting} className="btn-primary disabled:opacity-60" data-testid="app-submit-btn">
                          {submitting ? (
                            <>
                              <Loader2 size={15} className="animate-spin" /> Submitting…
                            </>
                          ) : (
                            <>Submit Application <ArrowRight size={15} /></>
                          )}
                        </button>
                      )}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
      <Footer />
    </div>
  );
}
